import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import api from '../config/api';

export default function ImportJournal() {
  const [rawRows, setRawRows] = useState([]);
  const [validRows, setValidRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [accountCodes, setAccountCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  const requiredFields = [
    "transaction_date",
    "account_code_id",
    "description",
    "amount",
    "transaction_type",
    "reference_number"
  ];

  const fetchAccountCodes = async () => {
    const token = localStorage.getItem("token");
    const res = await fetch(api("/account_codes/"), {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    setAccountCodes(data);
  };

  useEffect(() => {
    fetchAccountCodes();
  }, []);

  const parseRupiah = (val) => {
    if (!val || val === "Rp-" || val === "-") return 0;
    return parseInt(String(val).replace(/[^\d]/g, ""), 10) || 0;
  };

  const parseDate = (val) => {
    // Cek jika val adalah angka Excel date code
    if (typeof val === "number") {
      const excelDate = XLSX.SSF.parse_date_code(val);
      const year = excelDate.y, month = String(excelDate.m).padStart(2, "0"), day = String(excelDate.d).padStart(2, "0");
      return `${year}-${month}-${day}T00:00:00Z`;
    }

    // Kalau string, parse seperti biasa
    try {
      return new Date(val).toISOString();
    } catch {
      return null;
    }
  };


  const getInvalidReason = (row) => {
    const requiredFields = ["transaction_date", "account_code_id", "description", "amount", "transaction_type", "reference_number"];
    const missing = requiredFields.filter((key) => !row[key] && row[key] !== 0);
    return missing.length > 0 ? `Missing: ${missing.join(", ")}` : "";
  };

  const normalizeKeys = (row) => {
    const normalized = {};
    Object.keys(row).forEach((key) => {
      const cleanKey = key.trim().toLowerCase();
      normalized[cleanKey] = row[key];
    });
    return normalized;
  };


  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);

    const reader = new FileReader();
    reader.onload = async (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      const parsed = rows.map((originalRow) => {
        const row = normalizeKeys(originalRow);
        const coaCode = row["coa"];
        const account = accountCodes.find((ac) => ac.code === coaCode);
        const account_code_id = account?.id;
        const debit = parseRupiah(row["debit"]);
        const credit = parseRupiah(row["kredit"]);
        const amount = debit || credit;
        const transaction_type = debit > 0 ? "debit" : credit > 0 ? "credit" : null;

        const record = {
          transaction_date: parseDate(row["tanggal"]),
          account_code_id,
          description: row["keterangan"] || "",
          amount,
          transaction_type,
          reference_number: row["reference number"],
          payment_proof_url: "https://example.com/imported.jpg",
          supplier_name: "IMPORTED",
        };

        record.invalid_reason = getInvalidReason(record);
        return record;
      });

      const valids = parsed.filter((r) => !r.invalid_reason);
      const invalids = parsed.filter((r) => r.invalid_reason);

      setRawRows(parsed);
      setValidRows(valids);
      setInvalidRows(invalids);
      setLoading(false);
    };

    reader.readAsArrayBuffer(file);
  };


  const handleImport = async () => {
    const token = localStorage.getItem("token");
    const user_id = JSON.parse(atob(token.split(".")[1]))?.user_id;
    let success = 0;

    for (const row of validRows) {
      const payload = { 
        ...row, 
        user_id,
        correction_for: null };
      console.log(payload)
      const res = await fetch(api("/transactions/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) success++;
    }

    alert(`${success} transaksi berhasil diimport`);
  };

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <Sidebar />
        <div className="layout-page">
          <Navbar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <h3 className="mb-4">Import Journal</h3>
              <div className="card p-4">
                <input type="file" className="form-control mb-3" accept=".xlsx" onChange={handleUpload} />
                {loading && <p>⏳ Loading and parsing...</p>}

                {!loading && rawRows.length > 0 && (
                  <>
                    <p className="mb-2">
                      ✅ Valid: {validRows.length} | ❌ Invalid: {invalidRows.length}
                    </p>
                    {validRows.length > 0 && (
                      <button className="btn btn-primary mb-3" onClick={handleImport}>
                        Import Valid Transactions
                      </button>
                    )}

                    <div className="table-responsive" style={{ maxHeight: 400 }}>
                      <table className="table table-bordered small">
                        <thead className="table-light">
                          <tr>
                            <th>Tanggal</th>
                            <th>Account Code</th>
                            <th>Description</th>
                            <th>Amount</th>
                            <th>Type</th>
                            <th>Reference</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rawRows.map((row, i) => (
                            <tr key={i} className={getInvalidReason(row) ? "table-danger" : ""}>
                              <td>{row.transaction_date}</td>
                              <td>{row.account_code_id}</td>
                              <td>{row.description}</td>
                              <td>Rp {row.amount?.toLocaleString("id-ID")}</td>
                              <td>{row.transaction_type}</td>
                              <td>{row.reference_number}</td>
                              <td>
                                {getInvalidReason(row) ? (
                                  <span className="badge bg-danger d-flex align-items-center gap-1">
                                    Invalid
                                    <i
                                      className="bx bx-info-circle text-white"
                                      title={getInvalidReason(row)}
                                      style={{ cursor: "pointer" }}
                                    ></i>
                                  </span>
                                ) : (
                                  <span className="badge bg-success">Valid</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
